import React, { useEffect } from 'react'
import StateComponents from '../../../../components/state-component/StateComponents'
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';

export type MeetingRoomData = {
    id_meeting_type: number | null;
    meeting_room_name: string;
    meeting_room_start_date: string;
    meeting_room_end_date: string;
    meeting_room_start_time: string;
    meeting_room_end_time: string;
    meeting_room_desc: string;
    is_meeting_online?: number;           // 0 | 1
    is_internal_person?: number;          // 0 | 1
    id_meeting_file_other: number | null;
    id_room_meeting: number | null;
    is_content?: boolean;
    id_content?: number | null;
    meeting_room_status: string;
    meeting_room_type: string;
    file: any;
};
export type MeetingRoomError = {
    [K in keyof MeetingRoomData]: string;
};
type Props = {
    formState: MeetingRoomData,
    setFormState?: React.Dispatch<React.SetStateAction<MeetingRoomData>>;
    errorMessage?: Partial<MeetingRoomError>;
    clearErrorMessage?: () => void;
    disabled?: boolean;
    handleDownloadFile?: (id: number, file_name: string) => void;
    hidden_detail_send_request?: boolean;
    is_personal?: boolean;
}



const OtherDetail: React.FC<Props> = ({ disabled, formState, hidden_detail_send_request, is_personal, setFormState, errorMessage, clearErrorMessage, handleDownloadFile }: Props) => {
    const { t } = useTranslation();
    const profile_data: any = useSelector((state: RootState) => state.profile.profile);

    const handleChangeFormState = <K extends keyof MeetingRoomData>(field: K, value: MeetingRoomData[K]) => {
        clearErrorMessage?.();
        setFormState?.((prev) =>
            produce(prev, (draft) => {
                draft[field] = value;
            })
        );
    };

    return (
        <div>
            <StateComponents
                sxContainer={{ mt: 2 }}
                disabledAllField={disabled}
                otherProps={{ spacing: 2 }}
                typeProps={[
                    {
                        seq: 1,
                        type: "DATE",
                        gridProps: {
                            xs: 12,
                            md: 6,
                            xl: 3
                        },
                        fieldProps: {
                            onChange: (e) => handleChangeFormState('meeting_room_start_date', e),
                            value: formState.meeting_room_start_date,
                            placeholder: t("ระบุวันที่เริ่มต้น"),
                            heading: t("วันที่เริ่มต้น"),
                            helperText: errorMessage?.meeting_room_start_date
                        }
                    },
                    {
                        seq: 2,
                        type: "DATE",
                        gridProps: {
                            xs: 12,
                            md: 6,
                            xl: 3
                        },
                        fieldProps: {
                            disableStartDate: formState.meeting_room_start_date,
                            onChange: (e) => handleChangeFormState('meeting_room_end_date', e),
                            value: formState.meeting_room_end_date,
                            placeholder: t("ระบุวันที่เริ่มต้น"),
                            heading: t("วันที่สิ้นสุด"),
                            disabled: !formState.meeting_room_start_date,
                            helperText: errorMessage?.meeting_room_end_date
                        }
                    },
                    {
                        seq: 3,
                        type: "TIME_PICKER",
                        gridProps: {
                            xs: 12,
                            md: 6,
                            xl: 3
                        },
                        fieldProps: {
                            onChange: (e) => handleChangeFormState('meeting_room_start_time', e),
                            value: formState.meeting_room_start_time,
                            placeholder: t("ระบุเวลาเริ่มต้น"),
                            heading: t("เวลาเริ่มต้น"),
                            helperText: errorMessage?.meeting_room_start_time
                        }
                    },
                    {
                        seq: 4,
                        type: "TIME_PICKER",
                        gridProps: {
                            xs: 12,
                            md: 6,
                            xl: 3
                        },
                        fieldProps: {
                            onChange: (e) => handleChangeFormState('meeting_room_end_time', e),
                            value: formState.meeting_room_end_time,
                            placeholder: t("ระบุเวลาสิ้นสุด"),
                            heading: t("เวลาสิ้นสุด"),
                            helperText: errorMessage?.meeting_room_end_time
                        }
                    },
                    {
                        seq: 5,
                        type: "TEXT_FIELD",
                        gridProps: {
                            xs: 12,
                        },
                        fieldProps: {
                            onChange: (e) => handleChangeFormState('meeting_room_name', e.target.value),
                            value: formState.meeting_room_name,
                            placeholder: t("ระบุชื่อเรื่อง"),
                            heading: t("ชื่อเรื่อง"),
                            helperText: errorMessage?.meeting_room_name
                        }
                    },
                    {
                        seq: 6,
                        type: "TEXT_FIELD",
                        gridProps: {
                            xs: 12,
                        },
                        fieldProps: {
                            onChange: (e) => handleChangeFormState('meeting_room_desc', e.target.value),
                            value: formState.meeting_room_desc,
                            placeholder: t("ระบุรายละเอียด"),
                            heading: t("รายละเอียด"),
                            inputHeight: "auto",
                            row: 4,
                            maxRows: 4,
                            multiline: true,
                            helperText: errorMessage?.meeting_room_desc
                        }
                    },
                    {
                        seq: 7,
                        type: "UPLOAD-FILE",
                        gridProps: {
                            xs: 12,
                        },
                        fieldProps: {
                            onDrop: (file) => {
                                handleChangeFormState('file', file)
                            },
                            onlyPdf: true,
                            onDeleteFile: () => {
                                handleChangeFormState('file', null)
                            },
                            heading: t("ไฟล์แนบ"),
                            ...(disabled ? {} : {
                                onDeleteFileId: () => {
                                    handleChangeFormState('file', null)
                                },
                            }),
                            onDownloadFile: () => {
                                formState?.file?.id_meeting_room_file && handleDownloadFile?.(formState?.file?.id_meeting_room_file, formState?.file?.file_name)
                            },
                            noClick: formState?.file?.id_meeting_room_file,
                            fileAttachmentId: formState?.file?.id_meeting_room_file || "",
                            fileAttachmentName: formState?.file?.file_name || "",
                            file: formState.file,
                            helperText: errorMessage?.file
                        }
                    },
                ]}
            />

            {!hidden_detail_send_request && (
                <StateComponents
                    sxContainer={{ my: 2 }}
                    disabledAllField
                    otherProps={{ spacing: 2 }}
                    heading={t("ข้อมูลผู้ทำรายการจอง")}
                    typeProps={[
                        // {
                        //     seq: 1,
                        //     type: "AUTO-COMPLETE",
                        //     gridProps: {
                        //         xs: 12, md: 6, xl: 2,
                        //     },
                        //     fieldProps: {
                        //         getOptionLabel: () => "",
                        //         labelId: "",
                        //         heading: "คำนำหน้า",
                        //         onChange: () => { },
                        //         options: [],
                        //         value: null
                        //     }
                        // },
                        {
                            seq: 2,
                            type: "TEXT_FIELD",
                            gridProps: {
                                xs: 12, md: 6, xl: 6
                            },
                            fieldProps: {
                                value: profile_data?.member_name || "",
                                heading: t("ชื่อ")
                            }
                        },
                        {
                            seq: 3,
                            type: "TEXT_FIELD",
                            gridProps: {
                                xs: 12, md: 6, xl: 6
                            },
                            fieldProps: {
                                value: profile_data?.member_last_name || "",
                                heading: t("นามสกุล")
                            }
                        },
                        {
                            seq: 4,
                            type: "TEXT_FIELD",
                            gridProps: {
                                xs: 12, md: 6,
                            },
                            fieldProps: {
                                value: profile_data?.member_mobile || "",
                                heading: t("เบอร์โทร")
                            }
                        },
                        {
                            seq: 5,
                            type: "TEXT_FIELD",
                            gridProps: {
                                xs: 12, md: 6,
                            },
                            fieldProps: {
                                value: profile_data?.member_email || "",
                                heading: t("อีเมล")
                            }
                        },
                    ]}
                />
            )}
        </div>
    )
}

export default OtherDetail